import { AddAPhoto } from '@mui/icons-material';
import { Box, type SxProps } from '@mui/material';
import { type FC, memo } from 'react';
import type { MediaAttachment } from '../../../../features/messages/models/attachment';
import {
  MEDIA_LAYOUT_CONSTANTS,
  getSingleImageDimensions,
  selectLayout,
  sortImagesForLayout
} from '../../utils/image-layout-utils';
import MediaItem from './media-item';

interface MediaLayoutProps {
  attachments?: MediaAttachment[];
  sx?: SxProps;
  mode?: 'compact' | 'comfortable';
}

const MediaLayout: FC<MediaLayoutProps> = memo(({ attachments = [], sx, mode = 'comfortable' }) => {
  const count = attachments?.length;

  if (!attachments || count === 0) return null;

  // Sort and limit to max 5 visible images
  const visibleAttachments = attachments.slice(0, 5);
  const sortedAttachments = sortImagesForLayout(visibleAttachments);
  const hasMore = count > 5;

  // Get optimal layout configuration
  const layout = selectLayout(sortedAttachments);

  // Determine container sizing based on count
  const getContainerStyle = () => {
    const config = MEDIA_LAYOUT_CONSTANTS[mode];

    if (count === 1) {
      const img = sortedAttachments[0];
      const dims = getSingleImageDimensions(img.width, img.height, mode);
      return {
        width: dims.width,
        height: dims.height,
        maxWidth: config.single.maxWidth,
        maxHeight: config.single.maxHeight
      };
    }

    // Multi-image: fixed height grid
    return {
      height: config.multi.height,
      maxWidth: config.multi.maxWidth,
      width: '100%'
    };
  };

  return (
    <Box
      sx={{
        ...getContainerStyle(),
        ...sx,
        overflow: 'hidden'
      }}
      className={layout.containerClass}
    >
      {sortedAttachments.slice(0, hasMore ? 4 : 5).map((attachment, idx) => (
        <MediaItem
          key={attachment.id}
          attachment={attachment}
          className={layout.itemClasses[idx]}
          objectFit={layout.objectFits[idx]}
        />
      ))}

      {/* Show +N indicator for more than 5 images */}
      {hasMore && (
        <Box
          className='mex:relative mex:flex mex:items-center mex:justify-center mex:gap-2 mex:bg-black/40 mex:font-semibold mex:text-2xl mex:text-white'
          sx={{ gridArea: layout.itemClasses[4] ? 'auto' : undefined }}
        >
          <AddAPhoto /> +{count - 4}
        </Box>
      )}
    </Box>
  );
});

export default MediaLayout;
